import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MetricCard from './components/MetricCard';
import ActivityFeed from './components/ActivityFeed';
import CostTrendChart from './components/CostTrendChart';
import QuickActions from './components/QuickActions';
import NotificationPanel from './components/NotificationPanel';
import StoreStatusGrid from './components/StoreStatusGrid';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import {createClient} from "@supabase/supabase-js";

const DashboardOverview = () => {

  const [layouts, setLayouts] = useState([]);

  const supabase = createClient(import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);

  async function getLayouts() {
    const { data } = await supabase.from("store_layouts").select();
    setLayouts(data);
  }

  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for dashboard metrics
  const dashboardMetrics = [
    {
      title: "Active Branding Items",
      value: "1,247",
      subtitle: "Across 45 stores",
      icon: "Package",
      trend: "up",
      trendValue: "+12%",
      color: "primary"
    },
    {
      title: "Pending Installations",
      value: "23",
      subtitle: "Awaiting completion",
      icon: "Clock",
      trend: "down",
      trendValue: "-8%",
      color: "warning"
    },
    {
      title: "Monthly Cost",
      value: "৳12,45,000",
      subtitle: "October 2025",
      icon: "DollarSign",
      trend: "up",
      trendValue: "+5.2%",
      color: "success"
    },
    {
      title: "Layout Updates Required",
      value: "7",
      subtitle: "Stores need attention",
      icon: "Layout",
      trend: "neutral",
      trendValue: "0%",
      color: "error"
    }
  ];

  // Mock data for recent activities
  const recentActivities = [
    {
      id: 1,
      type: "installation",
      title: "Billboard Installation Completed",
      description: "Large billboard installation at main entrance",
      store: "Dhanmondi Branch",
      timestamp: new Date(Date.now() - 1800000),
      status: "completed"
    },
    {
      id: 2,
      type: "upload",
      title: "Layout Documentation Updated",
      description: "New floor plan uploaded with furniture placement",
      store: "Gulshan Branch",
      timestamp: new Date(Date.now() - 3600000),
      status: "pending"
    },
    {
      id: 3,
      type: "approval",
      title: "Branding Cost Approved",
      description: "Window display branding cost of ৳45,000 approved",
      store: "Uttara Branch",
      timestamp: new Date(Date.now() - 7200000),
      status: "completed"
    },
    {
      id: 4,
      type: "expired",
      title: "Shelf Talker Expired",
      description: "Monthly shelf talker campaign has expired",
      store: "Mirpur Branch",
      timestamp: new Date(Date.now() - 10800000),
      status: "pending"
    },
    {
      id: 5,
      type: "update",
      title: "Store Size Updated",
      description: "Floor area updated to 2,500 sq ft after renovation",
      store: "Banani Branch",
      timestamp: new Date(Date.now() - 14400000),
      status: "completed"
    }
  ];

  // Mock data for cost trend chart
  const costTrendData = [
    { month: "May", totalCost: 980000, installations: 45 },
    { month: "Jun", totalCost: 1120000, installations: 52 },
    { month: "Jul", totalCost: 1050000, installations: 48 },
    { month: "Aug", totalCost: 1180000, installations: 58 },
    { month: "Sep", totalCost: 1220000, installations: 61 },
    { month: "Oct", totalCost: 1245000, installations: 63 }
  ];

  // Mock data for stores
  const storeData = [
    {
      id: 1,
      name: "Dhanmondi Branch",
      location: "Dhanmondi, Dhaka",
      status: "active",
      totalCost: 285000,
      brandingItems: 12,
      size: 2200,
      lastUpdated: "2025-09-28"
    },
    {
      id: 2,
      name: "Gulshan Branch",
      location: "Gulshan, Dhaka",
      status: "pending",
      totalCost: 320000,
      brandingItems: 15,
      size: 2800,
      lastUpdated: "2025-09-25"
    },
    {
      id: 3,
      name: "Uttara Branch",
      location: "Uttara, Dhaka",
      status: "active",
      totalCost: 195000,
      brandingItems: 8,
      size: 1800,
      lastUpdated: "2025-09-30"
    },
    {
      id: 4,
      name: "Mirpur Branch",
      location: "Mirpur, Dhaka",
      status: "expired",
      totalCost: 240000,
      brandingItems: 11,
      size: 2100,
      lastUpdated: "2025-09-20"
    },
    {
      id: 5,
      name: "Banani Branch",
      location: "Banani, Dhaka",
      status: "missing",
      totalCost: 175000,
      brandingItems: 7,
      size: 1600,
      lastUpdated: "2025-09-15"
    },
    {
      id: 6,
      name: "Wari Branch",
      location: "Wari, Dhaka",
      status: "active",
      totalCost: 210000,
      brandingItems: 9,
      size: 1900,
      lastUpdated: "2025-09-29"
    }
  ];

  useEffect(() => {
    // Mock notifications data
    const mockNotifications = [
      {
        id: 1,
        type: "expired",
        priority: "high",
        title: "3 Branding Items Expired",
        description: "Shelf talkers at Mirpur branch expired yesterday",
        fullDescription: "Shelf talkers at Mirpur branch expired yesterday. These items need immediate replacement to maintain brand visibility. The expired items include promotional shelf talkers for the electronics section, seasonal campaign materials, and product highlight displays.",
        timestamp: new Date(Date.now() - 86400000),
        actions: [
          { label: "Replace Now", variant: "default", icon: "RefreshCw", onClick: () => navigate('/branding-cost-calculator') },
          { label: "View Details", variant: "outline", onClick: () => navigate('/installation-tracking') }
        ]
      },
      {
        id: 2,
        type: "missing",
        priority: "medium",
        title: "Layout Documentation Missing",
        description: "7 stores require updated layout plans",
        fullDescription: "7 stores require updated layout plans including Banani, Wari, and 5 other locations. Current layouts are outdated and don't reflect recent renovations and furniture changes. This affects accurate branding placement calculations.",
        timestamp: new Date(Date.now() - 172800000),
        actions: [
          { label: "Upload Layouts", variant: "default", icon: "Upload", onClick: () => navigate('/store-layout-manager') }
        ]
      },
      {
        id: 3,
        type: "approval",
        priority: "medium",
        title: "Installation Photos Pending",
        description: "23 installation photos awaiting approval",
        fullDescription: "23 installation photos awaiting approval from various store locations. These photos document completed branding installations and need verification before marking installations as complete.",
        timestamp: new Date(Date.now() - 259200000),
        actions: [
          { label: "Review Photos", variant: "default", icon: "Eye", onClick: () => navigate('/installation-tracking?filter=pending') }
        ]
      }
    ];

    setNotifications(mockNotifications);
    setIsLoading(false);
    getLayouts()

  }, [navigate]);

  const handleMetricClick = (metric) => {
    switch (metric?.title) {
      case "Active Branding Items":
        navigate('/branding-cost-calculator');
        break;
      case "Pending Installations":
        navigate('/installation-tracking?filter=pending');
        break;
      case "Monthly Cost":
        navigate('/brand-analytics');
        break;
      case "Layout Updates Required":
        navigate('/store-layout-manager');
        break;
      default:
        break;
    }
  };

  const handleNotificationDismiss = (notificationId) => {
    setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
  };

  const handleStoreClick = (store) => {
    navigate(`/store-management?store=${store?.id}`);
  };

  const handleCostTrendFilter = (filters) => {
    console.log('Cost trend filters:', filters);
    // In a real app, this would update the chart data based on filters
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-pulse-subtle">
            <Icon name="Loader2" size={48} className="text-primary animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Breadcrumbs/>

        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard Overview</h1>
            <p className="text-muted-foreground mt-1">
              Monitor branding operations and key performance metrics across all store locations
            </p>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/brand-analytics')}>
              <Icon name="BarChart3" size={16}/>
              View Analytics
            </Button>
            <Button onClick={() => navigate('/branding-cost-calculator')}>
              <Icon name="Plus" size={16}/>
              New Branding Item
            </Button>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardMetrics?.map((metric, index) => (
              <MetricCard
                  key={index}
                  {...metric}
                  onClick={() => handleMetricClick(metric)}
              />
          ))}
        </div>
        <h1>Layouts</h1>
        <ul>
          {layouts?.map((instrument) => (
              <li key={instrument.uuid}>{instrument.store_id}</li>
          ))}
        </ul>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Left Column - Activities and Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            <ActivityFeed
                activities={recentActivities}
                onViewAll={() => navigate('/installation-tracking')}
            />
            <QuickActions/>
          </div>

          {/* Right Column - Cost Trends and Notifications */}
          <div className="lg:col-span-2 space-y-6">
            <CostTrendChart
                data={costTrendData}
                onFilterChange={handleCostTrendFilter}
            />
            <NotificationPanel
                notifications={notifications}
                onDismiss={handleNotificationDismiss}
                onViewAll={() => navigate('/installation-tracking?filter=notifications')}
            />
          </div>
        </div>

        {/* Store Status Grid */}
        <StoreStatusGrid
            stores={storeData}
            onStoreClick={handleStoreClick}
        />
      </main>
      <QuickActionButton/>
    </div>
  );
};

export default DashboardOverview;